import { useMutation, useQuery } from "@apollo/client/react";
import { CheckOutlined, CloseOutlined, DeleteOutlined } from "@ant-design/icons";
import { css } from "@linaria/core";
import { Alert, App, Button, Flex, Form, Modal, Select, Space } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentSession } from "../context/CurrentSessionContext.tsx";
import {
  GetVatRatePresetsDocument,
  type IngredientFragment,
  SetIngredientReferencePriceDocument,
  UnitType,
} from "../gql/generated/graphqlSchema.tsx";
import { useServiceError } from "../hooks/useServiceError.ts";
import { formatNumber } from "../utils/nutritionFormat.ts";
import { currencySymbol } from "../utils/priceFormat.ts";
import { LocaleNumberInput } from "./LocaleNumberInput.tsx";
import { QuantityUnitInput, type QuantityUnitValue } from "./QuantityUnitInput.tsx";

// The VAT-mode select must have a fixed width: content-sized, it would resize
// when the selection changes ("Без ДДС" vs "С ДДС") and the price input —
// the flexible item in the compact group — would visibly shrink/expand.
const vatModeSelect = css`
  width: 7rem;
  flex: none;
`;
const priceInput = css`
  width: 7rem;
  flex: none;
`;
const vatRateSelect = css`
  width: 9rem;
`;

type MeasurementUnit = {
  readonly id: string;
  readonly code: string;
  readonly nameEn: string;
  readonly nameBg: string;
  readonly unitType: UnitType;
};

type PriceIngredient = Pick<IngredientFragment, "id" | "name" | "referencePrice">;

type FormValues = {
  quantityUnit: QuantityUnitValue;
  price: number;
  vatMode: "with" | "without";
  vatRate: number | null;
};

export const IngredientReferencePriceModal = ({
  open,
  onClose,
  ingredient,
  units,
  onSaved,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly ingredient: PriceIngredient;
  readonly units: readonly MeasurementUnit[];
  readonly onSaved: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();

  const { data: presetsData } = useQuery(GetVatRatePresetsDocument, { skip: !open });
  const presets = presetsData?.vatRatePresets ?? [];
  const { team } = useCurrentSession();
  const currency = team?.currency;

  const [setPrice, { loading, error }] = useMutation(SetIngredientReferencePriceDocument);
  const errorMessage = useServiceError(error);

  const weightUnits = units.filter((u) => u.unitType === UnitType.Weight);
  const currentPrice = ingredient.referencePrice;
  const initialQuantityUnit: QuantityUnitValue = {
    quantity: currentPrice?.quantity ?? 1,
    unitId: currentPrice?.unitId ?? units.find((u) => u.code === "kg")?.id ?? "",
  };

  // New prices default to the first VAT preset, but the presets usually arrive
  // after the form has mounted — backfill once they load, unless the user has
  // already touched the field (clearing it means "no VAT").
  const defaultVatRate = presets[0]?.rate ?? null;
  useEffect(() => {
    if (
      open &&
      currentPrice == null &&
      defaultVatRate != null &&
      !form.isFieldTouched("vatRate") &&
      form.getFieldValue("vatRate") == null
    ) {
      form.setFieldsValue({ vatRate: defaultVatRate });
    }
  }, [open, currentPrice, defaultVatRate, form]);

  const initialValues = (): Partial<FormValues> =>
    currentPrice
      ? {
          quantityUnit: initialQuantityUnit,
          price: currentPrice.price,
          vatMode: currentPrice.includesVat ? "with" : "without",
          vatRate: currentPrice.vatRate ?? null,
        }
      : {
          quantityUnit: initialQuantityUnit,
          vatMode: "without",
          vatRate: presets[0]?.rate ?? null,
        };

  const handleOk = () => {
    void form.validateFields().then(async (values) => {
      const { quantity, unitId } = values.quantityUnit;
      if (quantity == null) return; // the field validator already blocks this
      await setPrice({
        variables: {
          ingredientId: ingredient.id,
          input: {
            quantity,
            unitId,
            price: values.price,
            includesVat: values.vatMode === "with",
            vatRate: values.vatRate,
          },
        },
      });
      void message.success(t("ingredients.price.saveDone"));
      onClose();
      onSaved();
    });
  };

  const handleRemove = async () => {
    await setPrice({ variables: { ingredientId: ingredient.id, input: null } });
    void message.success(t("ingredients.price.removeDone"));
    onClose();
    onSaved();
  };

  return (
    <Modal
      afterClose={() => form.resetFields()}
      cancelButtonProps={{ icon: <CloseOutlined /> }}
      confirmLoading={loading}
      destroyOnHidden
      // eslint-disable-next-line react/no-unstable-nested-components
      footer={(_, { OkBtn, CancelBtn }) => (
        <Flex gap="small" justify="end">
          {currentPrice != null && (
            <Button danger icon={<DeleteOutlined />} loading={loading} onClick={() => void handleRemove()}>
              {t("ingredients.price.removeButton")}
            </Button>
          )}
          <CancelBtn />
          <OkBtn />
        </Flex>
      )}
      okButtonProps={{ icon: <CheckOutlined /> }}
      okText={t("common.save")}
      onCancel={onClose}
      onOk={handleOk}
      open={open}
      title={`${t("ingredients.price.dialogTitle")} — ${ingredient.name}`}
    >
      {/* destroyOnHidden remounts the form (and QuantityUnitInput) on every
          open, so initial values always reflect the current price — works
          whether the modal is mounted persistently (ingredient tab) or on
          demand (recipe rows). */}
      <Form
        form={form}
        initialValues={initialValues()}
        labelCol={{ span: 8 }}
        layout="horizontal"
        onFinish={handleOk}
        wrapperCol={{ span: 16 }}
      >
        <Form.Item
          label={t("ingredients.price.quantity")}
          name="quantityUnit"
          required
          rules={[
            {
              validator: (_, value: QuantityUnitValue | undefined) =>
                value != null && value.quantity != null && value.quantity > 0 && value.unitId !== ""
                  ? Promise.resolve()
                  : Promise.reject(new Error(t("ingredients.price.quantityRequired"))),
            },
          ]}
        >
          <QuantityUnitInput units={weightUnits} />
        </Form.Item>
        <Form.Item label={t("ingredients.price.price")} required>
          <Space.Compact>
            <Form.Item name="price" noStyle rules={[{ required: true, message: t("ingredients.price.priceRequired") }]}>
              <LocaleNumberInput
                className={priceInput}
                suffix={currency == null ? undefined : currencySymbol(currency, i18n.language)}
              />
            </Form.Item>
            <Form.Item name="vatMode" noStyle>
              <Select
                className={vatModeSelect}
                options={[
                  { value: "without", label: t("ingredients.price.withoutVat") },
                  { value: "with", label: t("ingredients.price.withVat") },
                ]}
                popupMatchSelectWidth={false}
              />
            </Form.Item>
          </Space.Compact>
        </Form.Item>
        <Form.Item label={t("ingredients.price.vatRate")} name="vatRate">
          <Select
            allowClear
            className={vatRateSelect}
            options={presets.map((p) => ({
              value: p.rate,
              label: t("ingredients.price.vatPresetLabel", { rate: formatNumber(p.rate, i18n.language) }),
            }))}
            placeholder={t("ingredients.price.noVat")}
            popupMatchSelectWidth={false}
          />
        </Form.Item>
      </Form>
      {errorMessage != null && <Alert showIcon title={errorMessage} type="error" />}
    </Modal>
  );
};
